/**
 * Set by the maintenance responder the ALB routes to during a planned window.
 * See truebiz-infra/docs/maintenance-mode.md.
 */
export const MAINTENANCE_MODE_HEADER = "X-Maintenance-Mode";

const REDIRECTED_AT_KEY = "truebiz.maintenanceRedirectedAt";

/**
 * How long one redirect suppresses the next.
 *
 * The guard is for a half-applied switch -- ALB swapped, CloudFront not --
 * where the page we navigate to is this same SPA, which would otherwise
 * redirect forever and hammer the API on every pass. It deliberately expires:
 * sessionStorage outlives a window, so a marker that was only ever set would
 * strand a long-lived tab in a broken SPA through the *next* window. Ten
 * minutes is short against a window measured in hours and long enough that a
 * reload loop is a handful of page loads rather than a spin.
 */
const REDIRECT_COOLDOWN_MS = 10 * 60 * 1000;

/**
 * True when a rejected request came from the maintenance responder rather than
 * from the application.
 *
 * `BaseApi.fetch` throws the `Response` itself on a non-ok status, so errors
 * carry both the status and the headers. Both are checked: a 503 on its own is
 * an ordinary outage, and treating one as a planned window would send users to
 * a "back shortly" page during an incident nobody scheduled.
 *
 * Reading the header cross-origin depends on the responder returning
 * `Access-Control-Expose-Headers: Retry-After, X-Maintenance-Mode` --
 * X-Maintenance-Mode is not CORS-safelisted, so without that the browser hides
 * it and every window looks like a bare 503. The responder sets it today
 * (truebiz-infra lambdas/maintenance_responder/index.py, `_cors`); this comment
 * is here because nothing in this repo can fail if that stops being true.
 */
export function isMaintenanceError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  const response = error as Partial<Response>;
  if (response.status !== 503) return false;

  // Stubbed responses in tests carry no Headers object.
  return response.headers?.get?.(MAINTENANCE_MODE_HEADER) === "1";
}

/**
 * Leave the SPA for the static maintenance page.
 *
 * There is nothing to render: during a window CloudFront's origin *is* the
 * maintenance bucket, so navigating away is all it takes. This only exists for
 * sessions that loaded their bundle before the origin was swapped, which would
 * otherwise sit there turning every API call into an opaque network error.
 *
 * A fresh query string rather than `location.reload()`, because there is no
 * hard-refresh API -- `reload(true)`'s forceReload argument was dropped from
 * the spec and is ignored. A URL the browser has never seen cannot be served
 * from its cache, which matters for anyone still holding an index.html from
 * before Cache-Control was set on deploys.
 *
 * Rate-limited rather than once-per-session; see REDIRECT_COOLDOWN_MS.
 */
export function leaveForMaintenancePage(): void {
  try {
    const now = Date.now();
    // Number(null) and Number("junk") are 0 and NaN, both falsy, so a missing
    // or corrupted value reads as "no recent redirect" rather than blocking
    // one. A negative elapsed means the clock moved back under us; treat that
    // as expired too, so a resync cannot strand the tab.
    const last = Number(window.sessionStorage.getItem(REDIRECTED_AT_KEY));
    const elapsed = now - last;
    if (last && elapsed >= 0 && elapsed < REDIRECT_COOLDOWN_MS) return;

    window.sessionStorage.setItem(REDIRECTED_AT_KEY, String(now));
  } catch {
    // Storage can be unavailable (Safari private mode, blocked cookies).
    // Redirecting without the guard beats not redirecting at all: the static
    // page is the correct destination either way, and the loop only happens if
    // the switch is half-applied.
  }

  window.location.replace(`/?maintenance=${Date.now()}`);
}
